/**
 * HTTP-клиент для микрофронтендов
 *
 * Предоставляет единый интерфейс для HTTP-запросов:
 * - Автоматическое добавление JWT токена
 * - Обработка ошибок
 * - JSON сериализация/десериализация
 */

import type { ApiClient, ApiClientConfig } from './types'

/**
 * Создать API клиент для MF
 */
export function createApiClient(config: ApiClientConfig, getToken: () => string | null): ApiClient {
  const { baseUrl, timeout = 30000 } = config

  async function request<T>(
    method: string,
    url: string,
    data?: unknown,
    customConfig?: RequestInit
  ): Promise<T> {
    const token = getToken()

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...customConfig?.headers,
    }

    if (token) {
      ;(headers as Record<string, string>).Authorization = `Bearer ${token}`
    }

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      const response = await fetch(`${baseUrl}${url}`, {
        method,
        headers,
        body: data ? JSON.stringify(data) : undefined,
        signal: controller.signal,
        ...customConfig,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        const errorBody = await response.text()
        throw new ApiError(
          `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          errorBody
        )
      }

      // Проверяем, есть ли тело ответа
      const contentType = response.headers.get('content-type')
      if (contentType?.includes('application/json')) {
        return (await response.json()) as T
      }

      return undefined as T
    } catch (error) {
      clearTimeout(timeoutId)

      if (error instanceof ApiError) {
        throw error
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError('Request timeout', 408, '')
        }
        throw new ApiError(error.message, 0, '')
      }

      throw new ApiError('Unknown error', 0, '')
    }
  }

  return {
    get<T>(url: string, config?: RequestInit): Promise<T> {
      return request<T>('GET', url, undefined, config)
    },

    post<T>(url: string, data?: unknown, config?: RequestInit): Promise<T> {
      return request<T>('POST', url, data, config)
    },

    put<T>(url: string, data?: unknown, config?: RequestInit): Promise<T> {
      return request<T>('PUT', url, data, config)
    },

    delete<T>(url: string, config?: RequestInit): Promise<T> {
      return request<T>('DELETE', url, undefined, config)
    },
  }
}

/**
 * Класс ошибки API
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}
